import {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  type RefObject,
} from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'
import { GroundShadow } from './GroundShadow'
import { ModelLoadingOverlay } from './ModelLoadingOverlay'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { Furniture3DProps } from '~/types/furniture3D'
import { ViewerZoomControls } from '../ViewerZoomControls/ViewerZoomControls'
import { getProduct } from './products'
import type { Vec3 } from './products/types'
import { captureScreenshot } from '~/utils/screenshotUtils'
import { exportSceneAsGLB, triggerDownload } from '~/utils/exportModelUtils'
import { WebGLErrorBoundary } from '../WebGLErrorBoundary/WebGLErrorBoundary'
import { useSceneDebug } from './SceneDebugInspector'
import { detectGPUTier, getDPRForTier } from './three/gpuTier'
import { FpsReporter } from './three/FpsReporter'
import { StaticModeContext } from './three/staticMode'
import { StaticModeHint } from './StaticModeHint'
import { useClarityCanvasMirror } from './useClarityCanvasMirror'
import styles from './FurnitureViewer.module.css'

const SCREENSHOT_DEFAULTS = {
  format: 'image/png' as const,
  quality: 0.92,
}

const CANVAS_DEFAULTS = {
  fov: 60,
  near: 0.5,
  far: 1000,
  background: '#f5f5f5',
} as const

function ModelLoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[2, 1.5, 0.8]} />
      <meshStandardMaterial color="#cccccc" opacity={0.5} transparent />
    </mesh>
  )
}

/** Large invisible planes that catch background clicks for column deselection */
function BackgroundClickPlane({ onClick }: { onClick: () => void }) {
  return (
    <>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} onClick={onClick}>
        <planeGeometry args={[6000, 6000]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {/* Back wall */}
      <mesh position={[0, 300, -500]} onClick={onClick}>
        <planeGeometry args={[6000, 2000]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </>
  )
}

const FurnitureScene = memo(function FurnitureScene({
  width,
  height,
  depth,
  currentPlintHeight,
  selectedColor,
  sections,
  openingType,
  columns,
  columnConfigurations,
  columnConfigurationsWithOptions,
  columnWidths,
  columnPositions,
  furnitureType,
  selectedColumnIndex: externalSelectedColumnIndex,
  onColumnClick,
  onDeselectFunctionReady,
  isMirrored = false,
}: Furniture3DProps) {
  const product = getProduct(furnitureType)

  const [internalSelectedColumnIndex, setInternalSelectedColumnIndex] =
    useState<number | null>(null)

  const isExternallyControlled = externalSelectedColumnIndex !== undefined
  const selectedColumnIndex = isExternallyControlled
    ? externalSelectedColumnIndex
    : internalSelectedColumnIndex

  const handleColumnSelectionChange = useCallback(
    (index: number | null) => {
      if (!isExternallyControlled) {
        setInternalSelectedColumnIndex(index)
      }
      onColumnClick?.(index)
    },
    [isExternallyControlled, onColumnClick]
  )

  const deselectColumn = useCallback(() => {
    if (!isExternallyControlled) {
      setInternalSelectedColumnIndex(null)
    }
  }, [isExternallyControlled])

  useEffect(() => {
    onDeselectFunctionReady?.(deselectColumn)
  }, [onDeselectFunctionReady, deselectColumn])

  const handleBackgroundClick = useCallback(() => {
    if (!isExternallyControlled) {
      setInternalSelectedColumnIndex(null)
    }
    onColumnClick?.(null)
  }, [isExternallyControlled, onColumnClick])

  return (
    <>
      <product.Lights enableShadows shadowQuality="high" />

      <GroundShadow
        width={width + 550}
        depth={depth + 550}
        opacity={0.25}
        blur={1.5}
      />

      <BackgroundClickPlane onClick={handleBackgroundClick} />

      <Suspense fallback={null}>
        <product.Decorations
          width={width}
          height={height}
          depth={depth}
          onBackgroundClick={handleBackgroundClick}
        />
      </Suspense>

      <Suspense fallback={<ModelLoadingFallback />}>
        <product.Builder
          selectedColor={selectedColor}
          width={width}
          height={height}
          depth={depth}
          currentPlintHeight={currentPlintHeight}
          sections={sections}
          openingType={openingType}
          columns={columns}
          columnConfigurations={columnConfigurations}
          columnConfigurationsWithOptions={columnConfigurationsWithOptions}
          columnWidths={columnWidths}
          columnPositions={columnPositions}
          selectedColumnIndex={selectedColumnIndex}
          onColumnClick={handleColumnSelectionChange}
          isMirrored={isMirrored}
          furnitureType={furnitureType}
        />
      </Suspense>
    </>
  )
})

export interface FurnitureViewerRef {
  captureScreenshot: (
    format?: 'image/png' | 'image/jpeg',
    quality?: number
  ) => string | null
  exportModel: () => Promise<void>
}

interface ScreenshotContext {
  rendererRef: RefObject<THREE.WebGLRenderer | null>
  sceneRef: RefObject<THREE.Scene | null>
  cameraRef: RefObject<THREE.Camera | null>
  latestPropsRef: RefObject<{
    furnitureType: string | undefined
    width: number
  }>
}

function captureFurnitureScreenshot(
  ctx: ScreenshotContext,
  format: 'image/png' | 'image/jpeg',
  quality: number
): string | null {
  const renderer = ctx.rendererRef.current
  const scene = ctx.sceneRef.current
  const camera = ctx.cameraRef.current
  if (!renderer || !scene || !camera) return null

  const perspectiveCamera = camera as THREE.PerspectiveCamera
  if (perspectiveCamera.type !== 'PerspectiveCamera') return null

  const { furnitureType, width } = ctx.latestPropsRef.current
  const product = getProduct(furnitureType)
  const baseConfig = product.viewer

  const originalPosition = perspectiveCamera.position.clone()
  const originalQuaternion = perspectiveCamera.quaternion.clone()

  const cameraPosition: Vec3 =
    product.screenshot.getCameraPosition?.(width, baseConfig) ??
    baseConfig.cameraPosition

  perspectiveCamera.position.set(...cameraPosition)
  perspectiveCamera.lookAt(...baseConfig.target)
  perspectiveCamera.updateMatrixWorld()

  const screenshot = captureScreenshot(
    renderer,
    scene,
    perspectiveCamera,
    format,
    quality
  )

  perspectiveCamera.position.copy(originalPosition)
  perspectiveCamera.quaternion.copy(originalQuaternion)
  perspectiveCamera.updateMatrixWorld()

  return screenshot
}

const FurnitureViewerComponent = forwardRef<
  FurnitureViewerRef,
  Furniture3DProps
>((props, ref) => {
  const {
    width,
    selectedColor,
    height,
    depth,
    currentPlintHeight,
    sections,
    openingType,
    columns,
    columnConfigurations,
    columnConfigurationsWithOptions,
    columnWidths,
    columnPositions,
    furnitureType,
    selectedColumnIndex,
    onColumnClick,
    onDeselectFunctionReady,
    isMirrored = false,
    onShare,
    onExport,
  } = props

  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.Camera | null>(null)

  const orbitRef = useRef<OrbitControlsImpl>(null)

  const handleZoomIn = useCallback(() => {
    const controls = orbitRef.current
    if (!controls) return
    const dir = controls.object.position
      .clone()
      .sub(controls.target)
      .normalize()
    const dist = controls.object.position.distanceTo(controls.target)
    const newDist = Math.max(controls.minDistance, dist * 0.75)
    controls.object.position.copy(
      controls.target.clone().addScaledVector(dir, newDist)
    )
    controls.update()
  }, [])

  const handleZoomOut = useCallback(() => {
    const controls = orbitRef.current
    if (!controls) return
    const dir = controls.object.position
      .clone()
      .sub(controls.target)
      .normalize()
    const dist = controls.object.position.distanceTo(controls.target)
    const newDist = Math.min(controls.maxDistance, dist * 1.33)
    controls.object.position.copy(
      controls.target.clone().addScaledVector(dir, newDist)
    )
    controls.update()
  }, [])

  const latestPropsRef = useRef({ furnitureType, width })
  latestPropsRef.current = { furnitureType, width }

  const clarityMirrorImgRef = useRef<HTMLImageElement | null>(null)
  const scheduleClarityCapture = useClarityCanvasMirror({
    rendererRef,
    sceneRef,
    cameraRef,
    imgRef: clarityMirrorImgRef,
  })

  // Camera moves don't touch scene-pending state, so refresh the mirror on
  // every OrbitControls change (drag, programmatic zoom)
  const handleControlsChange = useCallback(() => {
    scheduleClarityCapture()
  }, [scheduleClarityCapture])

  useImperativeHandle(
    ref,
    () => ({
      captureScreenshot: (
        format: 'image/png' | 'image/jpeg' = SCREENSHOT_DEFAULTS.format,
        quality: number = SCREENSHOT_DEFAULTS.quality
      ) =>
        captureFurnitureScreenshot(
          { rendererRef, sceneRef, cameraRef, latestPropsRef },
          format,
          quality
        ),
      exportModel: async () => {
        const scene = sceneRef.current
        if (!scene) return

        const { furnitureType } = latestPropsRef.current
        const filename = `${furnitureType ?? 'furniture'}-config.glb`
        const glbData = await exportSceneAsGLB(scene)
        triggerDownload(glbData, filename)
      },
    }),
    []
  )

  const handleCanvasCreated = useCallback(
    ({
      gl,
      scene,
      camera,
    }: {
      gl: THREE.WebGLRenderer
      scene: THREE.Scene
      camera: THREE.Camera
    }) => {
      gl.outputColorSpace = THREE.SRGBColorSpace
      gl.toneMapping = THREE.ACESFilmicToneMapping
      gl.toneMappingExposure = 1.0
      gl.shadowMap.enabled = true
      gl.shadowMap.type = THREE.PCFSoftShadowMap
      gl.shadowMap.autoUpdate = true

      rendererRef.current = gl
      sceneRef.current = scene
      cameraRef.current = camera
    },
    []
  )

  const product = getProduct(furnitureType)
  const config = product.viewer
  const gpuTier = useMemo(() => detectGPUTier(), [])
  const dpr = useMemo(() => getDPRForTier(gpuTier), [gpuTier])

  // Runtime perf guard: any device that sustains a low frame rate is frozen
  // into static mode (no rotate, no door/drawer animation).
  const [staticMode, setStaticMode] = useState(false)
  const handlePerfDowngrade = useCallback(() => setStaticMode(true), [])

  const debug = useSceneDebug()

  // Config changes (color, sizes, columns…) re-render the scene without
  // necessarily touching scene-pending state — refresh the Clarity mirror
  useEffect(() => {
    scheduleClarityCapture()
  }, [
    scheduleClarityCapture,
    width,
    height,
    depth,
    currentPlintHeight,
    selectedColor,
    sections,
    openingType,
    columns,
    columnConfigurations,
    columnConfigurationsWithOptions,
    columnWidths,
    columnPositions,
    furnitureType,
    selectedColumnIndex,
    isMirrored,
  ])

  return (
    <WebGLErrorBoundary>
      <div className={styles.container}>
        {/* Behind the canvas: only Clarity replays (blank canvas) reveal it */}
        <img
          ref={clarityMirrorImgRef}
          className={styles.clarityMirror}
          alt=""
          aria-hidden="true"
          data-clarity-unmask="true"
        />
        <Canvas
          camera={{
            position: config.cameraPosition,
            fov: CANVAS_DEFAULTS.fov,
            near: CANVAS_DEFAULTS.near,
            far: CANVAS_DEFAULTS.far,
          }}
          shadows
          frameloop="demand"
          dpr={dpr}
          gl={{ powerPreference: 'high-performance', alpha: true }}
          className={styles.canvas}
          onCreated={handleCanvasCreated}
          style={{ background: 'transparent' }}
        >
          <FpsReporter gpuTier={gpuTier} onDowngrade={handlePerfDowngrade} />
          <OrbitControls
            ref={orbitRef}
            makeDefault
            onChange={handleControlsChange}
            enablePan={!staticMode}
            enableZoom={false}
            enableRotate={!staticMode}
            enableDamping={false}
            minDistance={config.minDistance}
            maxDistance={config.maxDistance}
            minAzimuthAngle={config.minAzimuthAngle}
            maxAzimuthAngle={config.maxAzimuthAngle}
            minPolarAngle={config.minPolarAngle}
            maxPolarAngle={config.maxPolarAngle}
            target={config.target}
          />

          <StaticModeContext.Provider value={staticMode}>
          <FurnitureScene
            depth={depth}
            height={height}
            width={width}
            columnConfigurationsWithOptions={columnConfigurationsWithOptions}
            selectedColor={selectedColor}
            currentPlintHeight={currentPlintHeight}
            sections={sections}
            openingType={openingType}
            columns={columns}
            columnConfigurations={columnConfigurations}
            columnWidths={columnWidths}
            columnPositions={columnPositions}
            furnitureType={furnitureType}
            selectedColumnIndex={selectedColumnIndex}
            onColumnClick={onColumnClick}
            onDeselectFunctionReady={onDeselectFunctionReady}
            isMirrored={isMirrored}
          />
          </StaticModeContext.Provider>

          {debug.canvasNode}
        </Canvas>
        {debug.overlayNode}
        <ModelLoadingOverlay />
        {staticMode && <StaticModeHint />}
        <ViewerZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onShare={onShare}
          onExport={onExport}
        />
      </div>
    </WebGLErrorBoundary>
  )
})

FurnitureViewerComponent.displayName = 'FurnitureViewerComponent'

export const FurnitureViewer = memo(FurnitureViewerComponent)
